import React, { useState } from 'react'

interface PrivateModeToggleProps {
  isPrivateMode: boolean
  onToggle: (isPrivate: boolean) => void
  disabled?: boolean
}

export default function PrivateModeToggle({ isPrivateMode, onToggle, disabled = false }: PrivateModeToggleProps) {
  const [showWarning, setShowWarning] = useState(false)

  const handleToggle = () => {
    if (!isPrivateMode && !showWarning) {
      setShowWarning(true)
      return
    }
    
    onToggle(!isPrivateMode)
    setShowWarning(false)
  }

  const handleConfirm = () => {
    onToggle(true)
    setShowWarning(false)
  }

  const handleCancel = () => {
    setShowWarning(false)
  }

  return (
    <div className="private-mode-toggle">
      <div className="toggle-container">
        <label className="toggle-label">
          <span className={`mode-indicator ${isPrivateMode ? 'private' : 'regular'}`}>
            {isPrivateMode ? (
              <>
                <span className="lock-icon">🔒</span>
                Private Mode
              </>
            ) : (
              <>
                <span className="lock-icon">🔓</span>
                Regular Mode
              </>
            )}
          </span>
          
          <button
            className={`toggle-switch ${isPrivateMode ? 'active' : ''}`}
            onClick={handleToggle}
            disabled={disabled}
            type="button"
          >
            <div className="toggle-slider"></div>
          </button>
        </label>
      </div>

      <div className="mode-description">
        {isPrivateMode ? (
          <span className="private-desc">
            🛡️ End-to-end encrypted - Only you and recipient can read messages
          </span>
        ) : (
          <span className="regular-desc">
            🔍 Standard encryption - Readable for moderation when reported
          </span>
        )}
      </div>

      {/* Private Mode Warning Modal */}
      {showWarning && (
        <div className="modal-overlay">
          <div className="warning-modal">
            <div className="modal-header">
              <h3>🔒 Enable Private Mode?</h3>
            </div>
            
            <div className="modal-content">
              <div className="warning-content">
                <div className="feature-list">
                  <h4>Private Mode Features:</h4>
                  <ul>
                    <li>✅ End-to-end encryption</li>
                    <li>✅ Only sender and receiver can read messages</li>
                    <li>✅ Perfect for sensitive business discussions</li>
                    <li>✅ Signal-level security</li>
                  </ul>
                </div>

                <div className="usage-terms">
                  <h4>⚠️ Terms of Use:</h4>
                  <ul>
                    <li>Private Mode is for <strong>legitimate business use only</strong></li>
                    <li>All communications must comply with federal, state, and local laws</li>
                    <li>No illegal activities, harassment, or policy violations</li>
                    <li>Users remain responsible for legal compliance regardless of encryption</li>
                    <li>Abuse may result in account suspension or termination</li>
                    <li>Vendor accounts have additional compliance requirements</li>
                  </ul>
                </div>

                <div className="legal-compliance">
                  <h4>⚖️ Legal Compliance Notice:</h4>
                  <div className="compliance-warning">
                    <p>
                      <strong>Important:</strong> While your messages are end-to-end encrypted and 
                      cannot be read by platform administrators, you remain fully responsible for 
                      ensuring all communications comply with applicable laws and regulations.
                    </p>
                    <ul>
                      <li>Kratom legality varies by jurisdiction - know your local laws</li>
                      <li>Business communications must follow commercial regulations</li>
                      <li>Medical claims about kratom are prohibited</li>
                      <li>Age verification and compliance with all platform terms required</li>
                    </ul>
                  </div>
                </div>

                <div className="technical-note">
                  <h4>🔒 Technical Details:</h4>
                  <ul>
                    <li>Messages encrypted with RSA-2048 + AES-256</li>
                    <li>Keys generated and stored locally on your device</li>
                    <li>Platform administrators cannot decrypt message content</li>
                    <li>Metadata (sender, receiver, timestamp) may still be accessible</li>
                  </ul>
                </div>

                <div className="data-retention">
                  <h4>📋 Data & Privacy:</h4>
                  <ul>
                    <li>Encrypted messages are permanently deleted when you delete conversations</li>
                    <li>Platform cannot recover deleted private messages</li>
                    <li>See our <a href="/privacy-policy" target="_blank">Privacy Policy</a> and <a href="/terms-of-service" target="_blank">Terms of Service</a> for details</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn-confirm"
                onClick={handleConfirm}
              >
                I Agree - Enable Private Mode
              </button>
              <button 
                className="btn-cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}